import { useState, useEffect, useMemo, useRef } from 'react';

import { useParams } from 'react-router-dom';

import { DocFactPickerFact } from './doc-fact-picker-fact';
import { DocChronologyDoc } from '../../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology-doc.entity';
import { useUpdateDocChronologyDocFacts } from '@/api/mutations/useUpdateDocChronologyDocFacts';
import { useGetDocFacts } from '@/api/queries/useGetDocFacts';
import { FactDocViewer } from '@/new/components/doc-viewer/fact-doc-viewer';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { sortFactsByAppearance } from '@/new/lib/doc-viewer-utils';

interface DocFactPickerProps {
  chronDoc: DocChronologyDoc;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocFactPicker({ chronDoc, open, onOpenChange }: DocFactPickerProps) {
  const { matterId, subsetId, docChronologyId } = useParams();
  if (!matterId || !subsetId || !docChronologyId) return null;

  const [selectedFactIds, setSelectedFactIds] = useState<Set<string>>(new Set(chronDoc.facts.map((f) => f.factId)));
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [initialFactIds, setInitialFactIds] = useState<Set<string>>(new Set(chronDoc.facts.map((f) => f.factId)));
  const [currentFactId, setCurrentFactId] = useState<string | null>(null);

  // Sync selected state when dialog opens (in case facts were modified externally)
  useEffect(() => {
    if (open) {
      const initialIds = new Set(chronDoc.facts.map((f) => f.factId));
      setSelectedFactIds(initialIds);
      setInitialFactIds(initialIds);
    }
  }, [open, chronDoc.facts]);

  const hasChanges = useMemo(() => {
    if (selectedFactIds.size !== initialFactIds.size) return true;
    for (const id of selectedFactIds) {
      if (!initialFactIds.has(id)) return true;
    }
    return false;
  }, [selectedFactIds, initialFactIds]);

  const { data: facts, isLoading: isLoadingFacts } = useGetDocFacts(matterId, subsetId, chronDoc.docId, open);

  const sortedFacts = useMemo(() => sortFactsByAppearance(facts ?? [], chronDoc.docId), [facts, chronDoc]);
  const filteredFacts = useMemo(
    () => (showOnlySelected ? sortedFacts?.filter((fact) => selectedFactIds.has(fact.id)) : sortedFacts),
    [sortedFacts, showOnlySelected, selectedFactIds],
  );

  const { mutate: updateFacts, isLoading } = useUpdateDocChronologyDocFacts({
    onSuccess: () => {
      onOpenChange(false);
    },
  });

  const handleToggleFact = (factId: string) => {
    setCurrentFactId(factId);

    setSelectedFactIds((prev) => {
      const next = new Set(prev);
      if (next.has(factId)) {
        next.delete(factId);
      } else {
        next.add(factId);
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!matterId || !subsetId) return;

    updateFacts({
      matterId,
      subsetId,
      docChronologyId,
      docId: chronDoc.id,
      factIds: Array.from(selectedFactIds),
    });
  };

  const listRef = useRef(null);

  const scrollToFact = (id: string) => {
    setCurrentFactId(id);
    const element = document.getElementById(`fact-picker-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-full max-h-9/10 w-9/10 max-w-[1440px] flex-col gap-4 overflow-y-hidden rounded-2xl p-0">
        <div className="flex min-h-0 flex-1">
          <div className="m-5 basis-6/10 overflow-hidden rounded-md border">
            <FactDocViewer
              docId={chronDoc.docId}
              factId={currentFactId}
              hideCloseButton
              hideFeatureButtons
              showFactsByDefault
              onHighlightClick={scrollToFact}
            />
          </div>

          <div className="flex h-full basis-4/10 flex-col border-l bg-gray-100/90">
            <div className="px-6 pt-4">
              <div className="mx-1 mt-2 mb-1.25 flex items-center gap-2 text-base">
                <Icon className="lucide--search mb-px text-gray-400" />

                <p>
                  <span className="font-semibold text-gray-600">{selectedFactIds.size}</span> Selected{' '}
                  {selectedFactIds.size === 1 ? 'Fact' : 'Facts'}
                </p>
              </div>

              <div className="mx-1.25 flex items-center gap-2">
                <label className="flex cursor-pointer items-center gap-2.25 text-xs text-gray-600">
                  <Checkbox
                    checked={showOnlySelected}
                    onCheckedChange={(checked) => setShowOnlySelected(checked === true)}
                    className="mb-px bg-white"
                  />
                  Show only selected
                </label>

                <span className="text-xs text-gray-400">•</span>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedFactIds(new Set())}
                  disabled={selectedFactIds.size === 0}
                  className="-mb-px p-0 text-gray-500"
                >
                  Clear selection
                </Button>
              </div>
            </div>

            <div ref={listRef} className="mt-2 min-h-0 w-full flex-1 space-y-3 overflow-y-auto px-3 pt-2 pb-4">
              {isLoadingFacts ? (
                <>
                  <Skeleton className="block h-24 w-full rounded-lg bg-gray-200" />
                  <Skeleton className="block h-24 w-full rounded-lg bg-gray-200" />
                  <Skeleton className="block h-24 w-full rounded-lg bg-gray-200" />
                </>
              ) : filteredFacts.length === 0 ? (
                <p className="my-6 px-2 text-center text-sm text-gray-400">
                  {showOnlySelected ? 'No facts selected' : 'No facts found for this document'}
                </p>
              ) : (
                filteredFacts.map((fact) => (
                  <DocFactPickerFact
                    key={fact.id}
                    id={`fact-picker-${fact.id}`}
                    fact={fact}
                    isSelected={selectedFactIds.has(fact.id)}
                    onToggle={() => handleToggleFact(fact.id)}
                    onNavigate={() => setCurrentFactId(fact.id)}
                  />
                ))
              )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-4 border-t bg-white p-4">
              {hasChanges && (
                <p className="flex items-center gap-1.5 text-gray-500">
                  <Icon className="lucide--info" />
                  Unsaved changes
                </p>
              )}
              <Button variant={'primary'} onClick={handleSave} disabled={isLoading || !hasChanges}>
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
